import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalyticsPreview = () => {
  const [selectedMetric, setSelectedMetric] = useState('effectiveness');

  const analyticsData = {
    effectiveness: {
      title: 'Treatment Effectiveness Trends',
      value: '87%',
      change: '+5%',
      trend: 'up',
      description: 'Average effectiveness across all treatments this quarter',
      chartData: [78, 82, 85, 87, 89, 87]
    },
    compliance: {
      title: 'Compliance Status',
      value: '94%',
      change: '+2%',
      trend: 'up',
      description: 'Overall compliance rating for current period',
      chartData: [88, 90, 91, 93, 94, 94]
    },
    performance: {
      title: 'Team Performance',
      value: '96%',
      change: '+8%',
      trend: 'up',
      description: 'Task completion rate and efficiency metrics',
      chartData: [85, 88, 90, 92, 95, 96]
    },
    cost: {
      title: 'Cost Efficiency',
      value: '$3.2K',
      change: '-12%',
      trend: 'down',
      description: 'Cost savings compared to previous quarter',
      chartData: [4.5, 4.2, 3.9, 3.6, 3.4, 3.2]
    }
  };

  const currentData = analyticsData?.[selectedMetric];

  const renderMiniChart = (data) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    return (
      <div className="flex items-end space-x-1 h-16">
        {data?.map((value, index) => {
          const height = ((value - min) / range) * 100;
          return (
            <div
              key={index}
              className="flex-1 bg-primary/70 rounded-t transition-all hover:bg-primary"
              style={{ height: `${height}%`, minHeight: '10%' }}
            />
          );
        })}
      </div>
    );
  };

  const metrics = [
    { id: 'effectiveness', label: 'Effectiveness', icon: 'TrendingUp' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' },
    { id: 'performance', label: 'Performance', icon: 'Target' },
    { id: 'cost', label: 'Cost Savings', icon: 'DollarSign' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Analytics Preview</h3>
            <p className="text-sm text-muted-foreground">Interactive performance metrics and trends</p>
          </div>
          <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
            View Full Analytics
          </Button>
        </div>
      </div>

      <div className="p-6">
        {/* Metric Selector */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {metrics?.map(metric => (
            <button
              key={metric?.id}
              onClick={() => setSelectedMetric(metric?.id)}
              className={`p-4 rounded-lg border transition-all ${
                selectedMetric === metric?.id
                  ? 'border-primary bg-primary/10 shadow-sm'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Icon
                name={metric?.icon}
                size={20}
                className={selectedMetric === metric?.id ? 'text-primary' : 'text-muted-foreground'}
              />
              <p className={`text-sm font-medium mt-2 ${
                selectedMetric === metric?.id ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {metric?.label}
              </p>
            </button>
          ))}
        </div>

        {/* Selected Metric Display */}
        <div className="bg-muted/30 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-foreground">{currentData?.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">{currentData?.description}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-foreground">{currentData?.value}</p>
              <div className={`flex items-center justify-end space-x-1 mt-1 ${
                currentData?.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                <Icon name={currentData?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={16} />
                <span className="text-sm font-medium">{currentData?.change}</span>
              </div>
            </div>
          </div>

          {/* Mini Chart */}
          <div className="mt-6">
            {renderMiniChart(currentData?.chartData)}
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>6 months ago</span>
              <span>Current</span>
            </div>
          </div>
        </div>

        {/* Key Insights */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-success/10 rounded-lg border border-success/20">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <p className="text-sm font-medium text-success">Top Performer</p>
            </div>
            <p className="text-xs text-muted-foreground">Organic Neem Oil - 94% effectiveness</p>
          </div>
          
          <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-primary" />
              <p className="text-sm font-medium text-primary">Improvement</p>
            </div>
            <p className="text-xs text-muted-foreground">12% increase in field coverage</p>
          </div>
          
          <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Award" size={16} className="text-accent" />
              <p className="text-sm font-medium text-accent">Benchmark</p>
            </div>
            <p className="text-xs text-muted-foreground">Above industry average by 8%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPreview;